/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/naming-convention */
import { Inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  Concept,
  ConceptPopulated,
  DriveCycleInDB,
  DriveCycleRequirementIds,
  DriveCyclesService,
  DynamicRequirementInputsIds,
  JobsService,
  Requirement,
  RequirementsService as API,
  StaticRequirementAccelerationIds,
  UnitType,
  ApiError,
  JobStart,
} from 'src/api';
import { ConceptPartType, PartServiceType } from '../types';
import {
  AuthTokenState,
  SelectedAccountState,
  State,
} from '@ansys-internal/awf-angular/store';
import { PlotService } from './plot.service';
import { BehaviorSubject, lastValueFrom, throwError } from 'rxjs';
import { AWCTreeItem } from '@ansys-internal/awc-angular/trees';
import {
  AWF_LOCATION,
  AWFLocation,
  SnackBarService,
} from '@ansys-internal/awf-angular/shared';
import { AccountService } from '@ansys-internal/awf-angular/shared';
import { Job, JobApiService } from '@ansys/cloud-angular-client';
import { ResultType } from 'src/app/components/widgets/results-list/results-list.component';
import { DataDisplayService, DataDisplayState } from './data-display.service';
import { ConceptUnitService } from './unit.service';
import { AnalyticEventEnum } from '../enums';
import { ActionContributionsService } from '@ansys-internal/awf-angular/contributions';
import { SetJobs } from '../../actions';
import { ActiveConceptState } from 'src/app/state/lib/active-concept.state';
import { RequirementItemState } from 'src/app/state/lib/requirements.state';
import { DriveCycleAxisState } from 'src/app/state/lib/drive-cycle-axis.state';
import { NotificationService } from './notification.service';
import {
  AnalyticsEventPayload,
  SubmitAnalyticsEventAction,
} from '@ansys-internal/awf-angular/analytics';
import { JobLoadingStatus } from '../enums/job-loading-status.enum';
import { ProcessDriveCycleResultDataAction } from '../../actions/process-drive-cycle-result-data/process-drive-cycle-result-data.action';
import { AddDriveCycleAxisAction } from '../../actions/add-drive-cycle-axis/add-drive-cycle-axis.action';
import { SelectResultAction } from '../../actions/select-result/select-result.action';
import { SolvedComponentTableAction } from '../../actions/solved-component-table/solved-component-table.action';
import { driveCycleColumnsModel } from '../models/plot-data/drive-cycle-columns.model';
import { staticColumnsModel } from '../models/plot-data/static-columns.model';
import { PlotState } from '../../state/lib/plot.state';
import { Layout } from 'plotly.js';
import { AWCListItem } from '@ansys-internal/awc-angular/lists';
import { CapabilityCurveTypeEnum } from '../enums/capability-curve-type.enum';
import { ProcessResultDataAction } from '../../actions/process-result-data/process-result-data.action';
import { JobState } from '../../state/lib/jobs.state';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActiveTabState } from '@ansys-internal/awf-angular/workspace';

@Injectable({ providedIn: 'root' })
export class RequirementsService implements PartServiceType {
  public get columns(): string[] {
    return this.requirementColumns;
  }

  private $jobStatus = new BehaviorSubject<string>('');
  public jobStatus = this.$jobStatus.asObservable();
  public selectFromPlot: BehaviorSubject<string> = new BehaviorSubject<string>(
    ''
  );
  public selectedTimeIndex: BehaviorSubject<number | string> =
    new BehaviorSubject<number | string>(0);
  public jobResultLoading = new BehaviorSubject<JobLoadingStatus>(
    JobLoadingStatus.IDLE
  );

  public get data(): any[] {
    return this.mergedData;
  }
  private updatedItemSubject = new BehaviorSubject<AWCTreeItem | null>(null);
  updatedItem$ = this.updatedItemSubject.asObservable();

  private $selectedResult = new BehaviorSubject<any>(null);
  public selectedResult = this.$selectedResult.asObservable();
  private $selectedResultCliVersion = new BehaviorSubject<any>(null);
  public selectedResultCliVersion =
    this.$selectedResultCliVersion.asObservable();
  protected staticDynamicDefaultLayout: Partial<Layout> | undefined;
  protected dynamicResultData: any[] = [];
  protected staticResultData: any[] = [];
  protected mergedDriveCycles: any[] = [];
  protected driveCycleLayout: any[] = [];
  private requirementColumns: string[] = [];
  private conceptSignal = this.state.signal(ActiveConceptState);
  public mergedData: any[] = [];
  requirementYAxisSignal: WritableSignal<string> = signal(
    this.getRequirementYAxis()[0].id
  );
  get requirementYAxis(): WritableSignal<string> {
    return this.requirementYAxisSignal;
  }

  constructor(
    private api: API,
    private state: State,
    private plotService: PlotService,
    private jobService: JobsService,
    private snackbar: SnackBarService,
    private driveCycleService: DriveCyclesService,
    private accountService: AccountService,
    private dataDisplay: DataDisplayService,
    private unitChoices: ConceptUnitService,
    private jobApi: JobApiService,
    private actions: ActionContributionsService,
    private notifications: NotificationService,
    private http: HttpClient
  ) {}

  public async get(id: string, instanceId: string): Promise<Requirement> {
    return await lastValueFrom(
      this.api.readRequirementsItemIdGet(id, instanceId)
    );
  }

  public async addPart(item: ConceptPartType): Promise<any> {
    type requirementType =
      | DriveCycleRequirementIds
      | DynamicRequirementInputsIds
      | StaticRequirementAccelerationIds;
    const designId = (this.conceptSignal() as ConceptPopulated)
      .design_id as string;
    const part = await lastValueFrom(
      this.api.createRequirementsPost(
        {
          ...(item as requirementType),
        },
        designId
      )
    ).catch((err) => {
      this.notifications.error('Error Creating Requirement', err);
      throw new Error(err);
    });

    const type = (item as any).requirement_type;
    if (type) {
      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.REQUIREMENT_ADDED,
        data: { component_type: type },
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    }

    return part;
  }

  public async addDriveCycleFromFile(
    name: string = 'Drive Cycle',
    file: File
  ): Promise<DriveCycleInDB> {
    const designId = (this.conceptSignal() as ConceptPopulated)
      .design_id as string;
    return await lastValueFrom(
      this.driveCycleService.createFromFileDriveCyclesFromFilePost(
        name,
        {
          file,
        },
        designId
      )
    ).catch((err) => {
      this.notifications.error('Error Creating Drive Cycle', err);
      throw new Error(err);
    });
  }

  public resetDefaultLayoutRange(): void {
    this.plotService.updateGraphLayout({
      xaxis: { range: [0, 0] },
      yaxis: { range: [0, 0] },
    });
  }

  public async deletePart(item: AWCTreeItem): Promise<unknown> {
    const id = item.userData ? (item.userData['id'] as string) : null;
    if (!id) {
      throw new Error('No ID provided');
    }

    const designId = (this.conceptSignal() as ConceptPopulated)
      .design_id as string;

    const resp = await lastValueFrom(
      this.api.deleteRequirementsItemIdDelete(id, designId)
    ).catch((err) => {
      this.notifications.error('Error Deleting Requirement', err);
      throw new Error(err);
    });
    const currentState = this.state.value(
      RequirementItemState,
      this.state.value(ActiveTabState)?.id
    );
    const index = currentState.findIndex(
      (stateItem) => stateItem.id === item.id
    );
    currentState.splice(index, 1);
    this.state.set(
      RequirementItemState,
      currentState,
      this.state.value(ActiveTabState)?.id
    );

    if (item.userData?.['requirement_type']) {
      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.REQUIREMENT_DELETED,
        data: { component_type: item.userData?.['requirement_type'] },
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    }

    this.snackbar.success('Requirement Deleted!');
    return resp;
  }

  public async updatePart(item: AWCTreeItem): Promise<unknown> {
    const id = item.userData ? (item.userData['id'] as string) : null;
    if (!id) {
      throw new Error('No ID provided');
    }

    const designId = (this.conceptSignal() as ConceptPopulated)
      .design_id as string;
    const resp = await lastValueFrom(
      this.api.updateRequirementsItemIdPut(
        id,
        item.userData as
          | StaticRequirementAccelerationIds
          | DynamicRequirementInputsIds,
        designId
      )
    ).catch((err) => {
      this.snackbar.error('Error Updating Requirement!');
      throw new Error(err);
    });
    item.text =
      (resp as DynamicRequirementInputsIds | StaticRequirementAccelerationIds)
        ?.name || 'Drive Cycle';
    item.userData = {
      ...item.userData,
      ...resp,
    };

    const currentState = this.state.value(
      RequirementItemState,
      this.state.value(ActiveTabState)?.id
    );
    const index = currentState.findIndex(
      (stateItem) => stateItem.id === item.id
    );
    currentState[index] = item;
    this.state.set(
      RequirementItemState,
      currentState,
      this.state.value(ActiveTabState)?.id
    );

    if (item.userData?.['requirement_type']) {
      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.REQUIREMENT_UPDATED,
        data: { component_type: item.userData?.['requirement_type'] },
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    }

    this.updatedItemSubject.next(item);

    this.snackbar.success('Requirement Updated!');
    return resp;
  }

  public setJobStatus(status: string): void {
    this.$jobStatus.next(status);
  }

  public async solveRequirements(
    name: string,
    reqs: string[],
    extraMemory: boolean = false,
    dockerTag: string | undefined
  ): Promise<void> {
    reqs.length && (await this.fireJob(reqs, name, extraMemory, dockerTag));
  }

  protected async fireJob(
    requirements: string[],
    name: string,
    extraMemory: boolean = false,
    dockerTag: string | undefined
  ): Promise<void> {
    const concept = this.state.value(ActiveConceptState);
    const designId = this.conceptSignal()?.design_id as string;

    const requestBody = {
      job_name: name,
      requirement_ids: requirements,
      architecture_id: (concept as Concept)?.architecture_id as string,
      design_id: designId, // TODO: This should not be needed here as it's passed in 2nd arg of validateRequirementJobJobsValidatePost() below but we get an error if it's not here
    };

    await lastValueFrom(
      this.jobService
        .validateRequirementJobJobsValidatePost(requestBody, designId)
        .pipe(
          catchError((error: ApiError) => {
            const errorMessage =
              error.body?.detail || 'An unknown error occurred';

            this.snackbar.error(`Cannot create job: ${errorMessage}`);

            return throwError(() => error);
          })
        )
    );

    const [job, uploaded_file] = await lastValueFrom(
      this.jobService.createRequirementJobJobsPost(
        this.state.value(SelectedAccountState),
        requestBody,
        designId
      )
    ).catch((error: ApiError) => {
      const errorMessage = error.body?.message || 'An unknown error occurred';
      this.snackbar.error(`Cannot create job: ${errorMessage}`);
      throw error;
    });

    this.snackbar.info('Job Created');

    const hpc = await lastValueFrom(
      await this.accountService.getDefaultHPCAccount()
    );

    const requestBodyForm: JobStart = {
      job,
      uploaded_file,
      account_id: this.state.value(SelectedAccountState),
      hpc_id: hpc.hpcId as string,
      extra_memory: extraMemory,
    };

    if (dockerTag) {
      requestBodyForm.docker_tag = dockerTag;
    }

    await lastValueFrom(
      this.jobService.startJobJobsStartPost(
        this.state.value(SelectedAccountState),
        requestBodyForm,
        designId
      )
    ).catch((e) => {
      this.snackbar.error('Cannot start job');
      throw new Error(e);
    });

    const jobWithName = await lastValueFrom(
      this.jobApi.updateJobNameHandler({ jobId: job.id, jobName: name })
    ).catch((e) => {
      console.error(e);
    });

    if (jobWithName) {
      const jobs: Job[] = await this.state.value(JobState);
      const existingJob = jobs.find((jobLoop) => jobLoop.jobId === job.id);

      const updatedJobs = existingJob
        ? jobs.map((j) => (j.jobId === job.id ? { ...j, jobName: name } : j))
        : [jobWithName, ...jobs];

      this.actions.execute(SetJobs, updatedJobs);
    }
  }

  public async processDynamicResultData(results: any[]): Promise<void> {
    const [
      data,
      maxSpeed,
      maxTorque,
      maxPower,
      maxAcceleration,
      minSpeed,
      minTorque,
      minPower,
      minAcceleration,
    ] = await this.actions.execute(ProcessResultDataAction, [results, true]);

    this.setStaticDynamicDefaultLayout(
      maxSpeed,
      maxTorque,
      maxPower,
      maxAcceleration,
      minSpeed,
      minTorque,
      minPower,
      minAcceleration
    );
    this.dynamicResultData = data;
    this.mergedData = [...this.dynamicResultData, ...this.staticResultData];
  }

  public resetMergedData(): void {
    this.mergedData = [];
    this.staticResultData = [];
    this.dynamicResultData = [];
    this.staticDynamicDefaultLayout = undefined;
  }

  public setSelectedResult(result: any): void {
    this.$selectedResult.next(result);
  }

  public setSelectedResultCliVersion(cliVersion: string | undefined): void {
    this.$selectedResultCliVersion.next(cliVersion);
  }

  public async selectResult(
    result: any,
    cliVersion: string | undefined,
    driveCycle: boolean,
    selectedJob: string,
    getSelectedJob: () => string,
    forceRangeRefresh: boolean = false,
  ): Promise<void> {
    const plotData = await this.actions.execute(SelectResultAction, [
      result,
      driveCycle,
    ]);

    // also set the table data here from the solved components
    if (selectedJob.length && selectedJob === getSelectedJob()) {
      this.resetRequirementColumns(result.requirement_solved_type === ResultType.DRIVE_CYCLE);
      this.solvedComponentTable(result);
      this.plotService.setGraphData(plotData as any);

      if (driveCycle) {
        this.plotService.updateGraphLayout(
          this.filterDriveCycleLayout(result.id)
        );
        this.actions.execute(AddDriveCycleAxisAction, [
          result,
          this.state.value(DriveCycleAxisState, result.id) || [],
        ]);
      } else {
        let newLayout = this.staticDynamicDefaultLayout;

        const currentType = this.$selectedResult.value?.requirement_solved_type;
        const newType = result.requirement_solved_type;

        /** Only preserve layout if we're switching between static and dynamic */
        const isSameTypeGroup =
          ['static', 'dynamic'].includes(currentType) &&
          ['static', 'dynamic'].includes(newType);

        /** preserve axes when switching between static/dynamic results of the same job */
        if (
          !forceRangeRefresh &&
          result.jobId === this.$selectedResult.value?.jobId &&
          newType !== 'drive_cycle' &&
          currentType !== 'drive_cycle' &&
          isSameTypeGroup &&
          this.state.value(PlotState).layout?.xaxis !== undefined
        ) {
          const { xaxis, yaxis } = this.state.value(PlotState).layout!;

          newLayout = {
            ...this.staticDynamicDefaultLayout,
            xaxis,
            yaxis,
          };
        }

        await this.plotService.stripExtraYAxes();

        if (newLayout) {
          this.plotService.updateGraphLayout(newLayout);
        }
      }

      this.setSelectedResult(result);
      this.setSelectedResultCliVersion(cliVersion);
    }
  }

  private resetRequirementColumns(driveCycleResult?: boolean): void {
    this.requirementColumns = driveCycleResult
      ? driveCycleColumnsModel
      : staticColumnsModel;
  }

  public async processStaticResultData(results: any[]): Promise<void> {
    const plottableResults = results.filter((r) => r.capability_curve != null);
    if (!plottableResults.length) return;

    const [
      data,
      maxSpeed,
      maxTorque,
      maxPower,
      maxAcceleration,
      minSpeed,
      minTorque,
      minPower,
      minAcceleration,
    ] =
      // await this.actions.execute(ProcessStaticResultDataAction, results);
      await this.actions.execute(ProcessResultDataAction, [plottableResults]);

    this.setStaticDynamicDefaultLayout(
      maxSpeed,
      maxTorque,
      maxPower,
      maxAcceleration,
      minSpeed,
      minTorque,
      minPower,
      minAcceleration
    );

    this.staticResultData = data;
    this.mergedData = [...this.staticResultData, ...this.dynamicResultData];
  }

  private setStaticDynamicDefaultLayout(
    maxSpeed: number,
    maxTorque: number,
    maxPower: number,
    maxAcceleration: number,
    minSpeed: number,
    minTorque: number,
    minPower: number,
    minAcceleration: number
  ): void {
    const existingTopX = this.staticDynamicDefaultLayout?.xaxis?.range?.[1];
    const existingTopY = this.staticDynamicDefaultLayout?.yaxis?.range?.[1];
    const existingBottomX = this.staticDynamicDefaultLayout?.xaxis?.range?.[0];
    const existingBottomY = this.staticDynamicDefaultLayout?.yaxis?.range?.[0];

    const currentTopX = existingTopX ?? this.state.value(PlotState).layout?.xaxis?.range?.[1];
    const currentTopY = existingTopY ?? this.state.value(PlotState).layout?.yaxis?.range?.[1];
    const currentBottomX = existingBottomX ?? this.state.value(PlotState).layout?.xaxis?.range?.[0];
    const currentBottomY = existingBottomY ?? this.state.value(PlotState).layout?.yaxis?.range?.[0];

    // TODO: Move the following to an Action
    let title: Partial<import('plotly.js-dist-min').DataTitle>;
    let maxRange: number;
    let minRange: number;

    switch (this.requirementYAxis()) {
      case CapabilityCurveTypeEnum.POWER:
        title = { text: `Total Tractive Power (${this.unitChoices.getChoice(
          UnitType.POWER
        )})` };
        maxRange = maxPower;
        minRange = minPower;
        break;
      case CapabilityCurveTypeEnum.TORQUE:
        title = { text: `Total Tractive Torque (${this.unitChoices.getChoice(
          UnitType.TORQUE
        )})` };
        maxRange = maxTorque;
        minRange = minTorque;
        break;
      case CapabilityCurveTypeEnum.ACCELERATION:
        title = { text: `Acceleration (${this.unitChoices.getChoice(
          UnitType.ACCELERATION
        )})` };
        maxRange = maxAcceleration;
        minRange = minAcceleration;
        break;
      default:
        throw new Error('Unknown requirementYAxis value');
    }

    this.staticDynamicDefaultLayout = {
      xaxis: {
        range: [
          Math.min(minSpeed, currentBottomX, 0),
          Math.max(maxSpeed, currentTopX),
        ],
        title: { text: `Speed (${this.unitChoices.getChoice(UnitType.SPEED)})` },
      },
      yaxis: {
        range: [
          Math.min(minRange, currentBottomY, 0),
          Math.max(maxRange, currentTopY),
        ],
        title,
      },
      showlegend: true,
    };
  }

  public async displayData(): Promise<void> {
    // return this.solveRequirements();
  }

  public async processDriveCycleResults(results: any[]): Promise<void> {
    const [data, layouts] = await this.actions.execute(
      ProcessDriveCycleResultDataAction,
      results
    );
    this.mergedDriveCycles = data;
    this.driveCycleLayout = layouts;
  }

  filterDriveCycleLayout(id: string): any {
    return this.driveCycleLayout.find((layout) => layout.id === id);
  }

  public openTableFromPlot(data: any): void {
    if (data.data.id !== this.$selectedResult.value.id) {
      this.selectFromPlot.next(data.data.id);
    } else {
      this.dataDisplay.displayState = DataDisplayState.BLUEPRINT_DISPLAY;
      this.selectedTimeIndex.next(data.pointIndex);
    }
  }

  public async solvedComponentTable(
    result: any = this.$selectedResult.value,
    index: number = parseInt(this.selectedTimeIndex.value as string),
    columnFilter?: string[]
  ): Promise<void> {
    if (!columnFilter) {
      columnFilter = this.requirementColumns;
    } else {
      this.requirementColumns = columnFilter;
    }

    this.actions.execute(SolvedComponentTableAction, [
      result,
      index,
      columnFilter,
      this.requirementColumns,
    ]);
  }

  public filterDriveCycleData(id: string): Partial<any[]> {
    return this.mergedDriveCycles.filter((data) => {
      return data.id === id && data.x.length > 0 && data.y.length > 0;
    });
  }

  public getRequirementYAxis(
    capabilityCurve?: Record<string, number[]>
  ): AWCListItem[] {
    const acceleration = capabilityCurve?.['accelerations']
      ? {
          text: 'Acceleration',
          id: CapabilityCurveTypeEnum.ACCELERATION,
        }
      : null;

    return [
      {
        text: 'Torque',
        id: CapabilityCurveTypeEnum.TORQUE,
      },
      {
        text: 'Power',
        id: CapabilityCurveTypeEnum.POWER,
      },
      ...(acceleration ? [acceleration] : []),
    ] as AWCListItem[];
  }

  private fetchVersionList(): Promise<OcmCliVersionInfo[]> {
    return lastValueFrom(
      this.http.post<OcmCliVersionInfo[]>(
        `${environment.ocmUrl}/container/image/version/list`,
        {
          stage: environment.type,
          imageName: 'conceptev-hpc',
        },
        {
          responseType: 'json',
          headers: this.getHttpHeaders(),
        }
      )
    );
  }

  private getHttpHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `${this.state.value(AuthTokenState)}`,
    });
  }

  public async getVersions(): Promise<OcmCliVersionInfo[]> {
    try {
      return await this.fetchVersionList();
    } catch (error) {
      console.error('Error fetching version list:', error);
      return [];
    }
  }
}

interface OcmCliVersionInfo {
  traceId: string;
  dockerTagId: string;
  stage: string;
  imageName: string;
  version: string;
  createDate: string;
}
